import { useLocation, useNavigationType } from "react-router-dom";
import PageMeta from "../../components/common/PageMeta";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import { API_BASE_URL, DEFAULT_PAGE_DESCRIPTION, DEFAULT_PAGE_TITLE, DEFAULT_PER_PAGE } from "../../utilities/constants";
import { useEffect, useState, useRef } from "react";
import { handleApiCall } from "../../utilities/helper";
import Spinner from "../../components/ui/Spinner";
import GeneratedLooksCard from "./GeneratedLooksCard";
import Pagination from "../../components/ui/Pagination";
import useQueryParams from "../../hooks/useQueryParams";
import TableFilter from "./TableFilter";
import CustomModal from "../../components/ui/CustomModal";
import AdvancedFilterForm from "./AdvancedFilterForm";
import Button from "../../components/ui/button/Button";
import { FilterIcon } from "../../icons";
import ImageZoom from "../../components/common/ImageZoom";

const GeneratedLooks = () => {
    const location = useLocation();
    const navigationType = useNavigationType();

    const [lookData, setLookData] = useState(() => {
        if (navigationType === "POP") {
            const saved = sessionStorage.getItem("generatedLooks_data");
            return saved ? JSON.parse(saved) : null;
        } else {
            sessionStorage.removeItem("generatedLooks_data");
            return null;
        }
    });

    const [loading, setLoading] = useState(false);
    const [controller, setController] = useState(null);
    const { queryParams } = useQueryParams();
    const pageNo = queryParams.get("page") || 0;
    const pageSize = queryParams.get("pageSize") || DEFAULT_PER_PAGE;
    const sortOrder = queryParams.get("sortOrder") || "DESC";
    const [stylistId, setStylistId] = useState(() => {
        if (navigationType === "POP") {
            return sessionStorage.getItem("generatedLooks_stylistId") || null;
        } else {
            sessionStorage.removeItem("generatedLooks_stylistId");
            return null;
        }
    });
    const [advancedFilterParams, setAdvancedFilterParams] = useState(() => {
        if (navigationType === "POP") {
            const saved = sessionStorage.getItem("generatedLooks_filter");
            return saved ? JSON.parse(saved) : null;
        } else {
            sessionStorage.removeItem("generatedLooks_filter");
            return null;
        }
    });

    const callListApi = async () => {
        controller?.abort();
        const newController = new AbortController();
        setController(newController);
        const { signal } = newController;
        setLoading(true);
        try {
            let url =
                `${API_BASE_URL}/api/1.0.0/aigenlook/v3/look-listing` +
                `?pageNo=${pageNo}` +
                `&pageSize=${pageSize}` +
                `&sortOrder=${sortOrder}`;

            if (advancedFilterParams) {
                url = `${API_BASE_URL}/api/1.0.0/aigenlook/v3/look-listing-filter?`;
                //  +
                // `?pageNo=${pageNo}` +
                // `&pageSize=${pageSize}` +
                // `&sortOrder=${sortOrder}`;

                Object.keys(advancedFilterParams).forEach(key => {
                    if (advancedFilterParams[key] && key !== 'stylistName') {
                        url += `&${key}=${advancedFilterParams[key]}`;
                    }
                });
            } else if (stylistId) {
                url += `pageNo=${0}&stylistId=${stylistId}`;
            }

            const response = await handleApiCall(
                url,
                {
                    method: "GET"
                },
                null,
                signal
            );

            if (response.responseStatus == true) {
                // Normalize data structure
                let normalizedData = response.data;
                if (Array.isArray(response.data)) {
                    normalizedData = {
                        list: response.data,
                        pageNo: 0, // Default or manual handling
                        totalRecords: response.data.length // Default or manual handling
                    };
                }
                setLookData(normalizedData);
                sessionStorage.setItem("generatedLooks_data", JSON.stringify(normalizedData));
                return true
            } else {
                setLookData(null);
                sessionStorage.removeItem("generatedLooks_data");
            }
            if (response.responseCode !== 299) {
                setLoading(false);
            }
        } catch (e) {
            console.error("Error", e);
        } finally {
            setLoading(false);
        }
    };
    const isFirstMount = useRef(true);

    useEffect(() => {
        if (isFirstMount.current) {
            isFirstMount.current = false;
            // If we have restored data, DO NOT fetch.
            if (lookData) {
                return;
            }
        }
        callListApi();
    }, [location.search, stylistId, advancedFilterParams]);

    useEffect(() => {
        if (advancedFilterParams) {
            sessionStorage.setItem("generatedLooks_filter", JSON.stringify(advancedFilterParams));
        }
    }, [advancedFilterParams]);

    useEffect(() => {
        if (stylistId) {
            sessionStorage.setItem("generatedLooks_stylistId", stylistId);
        } else {
            sessionStorage.removeItem("generatedLooks_stylistId"); // Clear if null
        }
    }, [stylistId]);

    return (
        <>
            <PageMeta
                title={`Look Generation - ${DEFAULT_PAGE_TITLE}`}
                description={`Look Generation - ${DEFAULT_PAGE_DESCRIPTION}`}
            />
            <div className="flex justify-between items-center mb-4">
                <PageBreadcrumb
                    pageTitle="Tailored Looks"
                />
            </div>
            <div className="flex justify-between items-center mb-4">
                <TableFilter onStylistChange={setStylistId} />
                <div className="flex gap-2">
                    {advancedFilterParams && (
                        <div onClick={() => {
                            setAdvancedFilterParams(null);
                            sessionStorage.removeItem("generatedLooks_filter");
                            sessionStorage.removeItem("generatedLooks_data");
                            sessionStorage.removeItem("generatedLooks_stylistId");
                        }}>
                            <Button variant={advancedFilterParams ? "" : "cancel"}>
                                Clear Filters
                            </Button>
                        </div>
                    )}
                    <CustomModal
                        selector={
                            <Button
                                variant={advancedFilterParams ? "primary" : "secondary"}
                                startIcon={<FilterIcon className="w-5 h-5" />}
                            >
                                Advanced Filter
                            </Button>
                        }
                        content={
                            <AdvancedFilterForm
                                onSubmit={setAdvancedFilterParams}
                                initialValues={advancedFilterParams}
                                setShow={() => { }}
                            />
                        }
                    />
                </div>
            </div>
            {loading ? (
                <div className="flex justify-center items-center">
                    <Spinner />
                </div>
            ) : (
                <>
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-xl ">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">


                            {lookData?.list?.map((look) => (
                                <GeneratedLooksCard key={look?.lookId || look?.jobId} look={look} />
                            ))}

                        </div>
                    </div>
                    <div className='flex justify-center items-center'>
                        <Pagination
                            pageParam="page"
                            currentPage={lookData?.pageNo + 1}
                            totalElements={lookData?.totalRecords}
                        />
                    </div>
                </>
            )}
        </>
    );
}
export default GeneratedLooks;